import { useMemo } from 'react';
import { getFileViewerUrl, useTranslation } from '@packages/utils';
import { TColumnType } from 'components/Base/Table/Table.types';
import { DocumentIcon, SvgDocumentIcon } from 'assets/icon/icon';
import {
  PUBLIC_DISTRIBUTION,
  TInputOutputRow,
  TIoCategoryOption,
} from 'data/types/io.types';
import { useGetIoCategoriesQuery } from 'redux/apiSlice/io.slice';
import { ParagraphEllipsis } from 'components/Shared/ParagraphEllipsis';

type SourceType = 'ORGANIZATION' | 'ELC' | 'PEOPLE' | 'EVENT' | 'WORKSHOP';
type FilterItem = { text: string; value: string };

const TYPE_FILTERS: FilterItem[] = [
  { text: 'Money', value: 'MONEY' },
  { text: 'Materials', value: 'MATERIALS' },
  { text: 'Time', value: 'TIME' },
];

// One shared empty list: a fresh `= []` default on every render while the
// category queries load would give categoryFilters a new identity each render,
// re-running the OutputTab effect that depends on it in an endless loop.
const NO_CATEGORIES: TIoCategoryOption[] = [];

const useGetInputOutputColumnsData = (sourceType?: SourceType) => {
  const { t } = useTranslation();

  const { data: moneyCategories = NO_CATEGORIES } = useGetIoCategoriesQuery({
    type: 'MONEY',
  });
  const { data: materialsCategories = NO_CATEGORIES } = useGetIoCategoriesQuery(
    {
      type: 'MATERIALS',
    },
  );
  const { data: timeCategories = NO_CATEGORIES } = useGetIoCategoriesQuery({
    type: 'TIME',
  });

  const categoryFilters: FilterItem[] = useMemo(() => {
    const map = new Map<string, FilterItem>();
    [...moneyCategories, ...materialsCategories, ...timeCategories].forEach(
      opt => {
        if (!map.has(opt.value))
          map.set(opt.value, { text: opt.label, value: opt.value });
      },
    );
    return Array.from(map.values());
  }, [moneyCategories, materialsCategories, timeCategories]);

  const generateUniqueFilters = <K extends keyof TInputOutputRow>(
    items: TInputOutputRow[],
    key: K,
    valueKey?: keyof TInputOutputRow,
  ): FilterItem[] => {
    const filterMap = new Map<string, FilterItem>();

    items.forEach(item => {
      const text = String(item[key] ?? '');
      const value = String((valueKey ? item[valueKey] : item[key]) ?? '');

      if (text && !filterMap.has(value)) {
        filterMap.set(value, { text, value });
      }
    });

    return Array.from(filterMap.values());
  };

  const renderDocument = (hasDocument: boolean, record: TInputOutputRow) => {
    if (!hasDocument) return '-';

    const isSvg =
      !!record.documentIsSvg ||
      record.documentName?.toLowerCase().endsWith('.svg') ||
      record.documentUrl?.toLowerCase().endsWith('.svg');

    return (
      <a
        target="_blank"
        className="ellipsis"
        href={getFileViewerUrl(record.documentUrl)}
        rel="noreferrer"
      >
        {isSvg ? <SvgDocumentIcon /> : <DocumentIcon />}
      </a>
    );
  };

  const renderWithTooltip = (text: string) => (
    <ParagraphEllipsis text={text || '-'} textMaxLength={20} />
  );

  const getBaseColumns = (): TColumnType<TInputOutputRow>[] => [
    {
      title: t(
        'descriptions.admin-portal.input-output.table.DATE',
        'Activity Date',
      ),
      dataIndex: 'date',
      key: 'DATE',
      sorter: true,
      width: 150,
    },
    {
      title: t('descriptions.admin-portal.input-output.table.GIVING', 'Giving'),
      dataIndex: 'giving',
      key: 'GIVER',
      sorter: true,
      ellipsis: true,
      width: 220,
      render: renderWithTooltip,
      filterSearch: true,
      onHeaderCell: () => {
        return {
          className: 'giving-header',
          'data-cy': 'btn_filter_giving',
        } as any;
      },
    },
    {
      title: t(
        'descriptions.admin-portal.input-output.table.RECEIVING',
        'Receiving',
      ),
      dataIndex: 'receiving',
      key: 'RECEIVER',
      sorter: true,
      ellipsis: true,
      width: 220,
      render: renderWithTooltip,
      filterSearch: true,
      onHeaderCell: () => {
        return {
          className: 'receiving-header',
          'data-cy': 'btn_filter_receiving',
        } as any;
      },
    },
    {
      title: t('descriptions.admin-portal.input-output.table.TYPE', 'Type'),
      dataIndex: 'type',
      key: 'type',
      sorter: false,
      width: 130,
      onHeaderCell: () => {
        return {
          className: 'type-header',
          'data-cy': 'btn_filter_type',
        } as any;
      },
    },
    {
      title: t(
        'descriptions.admin-portal.input-output.table.CATEGORY',
        'Category',
      ),
      dataIndex: 'category',
      key: 'category',
      sorter: false,
      ellipsis: true,
      width: 260,
      render: renderWithTooltip,
      filterSearch: true,
      onHeaderCell: () => {
        return {
          className: 'category-header',
          'data-cy': 'btn_filter_category',
        } as any;
      },
    },
    {
      title: t('descriptions.admin-portal.input-output.table.AMOUNT', 'Amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'left',
      sorter: false,
      width: 120,
      onHeaderCell: () => {
        return {
          className: 'amount-header',
          'data-cy': 'btn_filter_amount',
        } as any;
      },
      render: (amount: string, record: TInputOutputRow) => {
        if (record.rawType === 'MONEY') {
          const numericAmount = parseFloat(amount) || 0;
          const formattedAmount = numericAmount.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });

          return `$${formattedAmount}`;
        }
        if (record.rawType === 'TIME') {
          if (record.timeHours != null || record.timeMinutes != null) {
            const h = record.timeHours ?? 0;
            const m = record.timeMinutes ?? 0;
            return `${h}h ${m}m`;
          }
        }
        return amount;
      },
    },
    {
      title: t(
        'descriptions.admin-portal.input-output.table.FAMILIES_SERVED',
        'Families served',
      ),
      dataIndex: 'familiesServed',
      // The key doubles as the backend sortField (GROW-4393)
      key: 'FAMILIES_SERVED',
      sorter: true,
      width: 150,
      onHeaderCell: () => {
        return {
          className: 'families-served-header',
          'data-cy': 'btn_sort_families_served',
        } as any;
      },
      // Only Public Distribution rows record a figure; null means "not
      // recorded", so it shows as '-' while a recorded 0 stays 0
      render: (familiesServed?: number | null) =>
        familiesServed != null ? familiesServed.toLocaleString('en-US') : '-',
    },
    {
      title: t('descriptions.admin-portal.input-output.table.NOTES', 'Notes'),
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      width: 320,
      render: renderWithTooltip,
    },
    {
      title: t(
        'descriptions.admin-portal.input-output.table.DOCUMENT',
        'Document',
      ),
      dataIndex: 'document',
      key: 'document',
      width: 90,
      render: renderDocument,
    },
  ];

  const getColumns = useMemo((): TColumnType<TInputOutputRow>[] => {
    const columns = getBaseColumns();
    return columns;
  }, [t, sourceType]);

  const updateColumnFilters = (
    column: TColumnType<TInputOutputRow>,
    dataIndex: keyof TInputOutputRow,
    apiFilters: any,
    items: TInputOutputRow[],
  ): TColumnType<TInputOutputRow> => {
    const filterMap: Record<string, () => FilterItem[]> = {
      giving: () =>
        apiFilters?.givers?.length
          ? apiFilters.givers
          : generateUniqueFilters(items, 'giving', 'giverId'),
      receiving: () =>
        apiFilters?.receivers?.length
          ? apiFilters.receivers
          : generateUniqueFilters(
              // A Public Distribution row has no receiver id; the backend
              // filters it by the PUBLIC_DISTRIBUTION key (GROW-4308)
              items.map(item =>
                item.receiverType === PUBLIC_DISTRIBUTION
                  ? { ...item, receiverId: PUBLIC_DISTRIBUTION }
                  : item,
              ),
              'receiving',
              'receiverId',
            ),
      type: () => TYPE_FILTERS,
      category: () =>
        categoryFilters.length > 0
          ? categoryFilters
          : generateUniqueFilters(items, 'category', 'categoryId'),
    };

    const getFilters = filterMap[dataIndex as string];
    if (getFilters) {
      return { ...column, filters: getFilters() };
    }

    return column;
  };

  const updateColumnsWithFilters = (
    columns: TColumnType<TInputOutputRow>[],
    data: any,
    items: TInputOutputRow[],
  ): TColumnType<TInputOutputRow>[] => {
    return columns.map(column => {
      const dataIndex = column.dataIndex as keyof TInputOutputRow;
      return updateColumnFilters(column, dataIndex, data?.filters, items);
    });
  };

  return {
    getColumns,
    updateColumnsWithFilters,
    generateUniqueFilters,
    getTypeFilters: () => TYPE_FILTERS,
    categoryFilters,
  };
};

export default useGetInputOutputColumnsData;
